import { useTranslation } from "react-i18next"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { getProjectFileStatusSummary } from "@/features/projects/utils/project-file-status"
import { useProjectEmbeddingStatus, useProjectFiles } from "@/hooks/use-projects"
import { cn } from "@/lib/utils"

type ProjectEmbeddingIndicatorProps = {
  projectId: string | undefined
  onClick?: () => void
  className?: string
}

export function ProjectEmbeddingIndicator({
  projectId,
  onClick,
  className,
}: ProjectEmbeddingIndicatorProps) {
  const { t } = useTranslation("", { keyPrefix: "project_settings.files" })

  const { data: files, isReindexPolling } = useProjectFiles(projectId)
  const localStatus = getProjectFileStatusSummary(files)
  const hasPendingFiles = localStatus.pendingFileCount > 0
  const hasLegacyFiles = files?.some((file) => file.needs_reupload === true)
  const hasFilesNotInVectorStore = files?.some(
    (file) =>
      file.vector_store_eligible !== false &&
      file.in_vector_store === false &&
      file.needs_reupload !== true
  )
  const { data: embeddingStatus } = useProjectEmbeddingStatus(projectId, hasPendingFiles)
  // A disabled TanStack query can retain an older polling response. Once the
  // fresh file list is terminal, ignore that cache so it cannot resurrect a
  // stale pending state.
  const activeEmbeddingStatus = hasPendingFiles ? embeddingStatus : undefined
  const status = getProjectFileStatusSummary(files, activeEmbeddingStatus)
  const { allFilesAreAnalysisReady, fileCount, indexedFileCount } = status
  const isEmbeddingInProgress = status.pendingFileCount > 0

  if (!projectId || fileCount === 0) {
    return null
  }

  // Determine status priority: embedding in progress > legacy files > not in vector store > complete
  const hasIssues = hasLegacyFiles || hasFilesNotInVectorStore

  let statusText: string
  if (isEmbeddingInProgress) {
    const remaining = status.pendingFileCount
    if (remaining === 1) {
      statusText = t("indexing_progress_singular", {
        remaining,
      })
    } else {
      statusText = t("indexing_progress_plural", {
        remaining,
      })
    }
  } else if (hasLegacyFiles) {
    statusText = t("has_legacy_files", { total: indexedFileCount })
  } else if (isReindexPolling) {
    // Vector store missing/expired and the backend has actually kicked off
    // eager re-indexing for this viewer (routes/projects.py::list_project_files)
    // -- reflect that it's genuinely in progress.
    statusText = t("vector_store_expired_tooltip")
  } else if (hasFilesNotInVectorStore) {
    // Not in the vector store, but not actively being re-indexed from this
    // viewer's session (reader, or the poll cap was hit) -- say so honestly
    // instead of claiming ongoing work that isn't happening.
    statusText = t("vector_store_needs_reindex_tooltip")
  } else if (allFilesAreAnalysisReady) {
    statusText =
      fileCount === 1
        ? t("analysis_ready_singular", { total: fileCount })
        : t("analysis_ready_plural", { total: fileCount })
  } else if (indexedFileCount === 1) {
    statusText = t("indexing_complete_singular", { total: indexedFileCount })
  } else {
    statusText = t("indexing_complete_plural", { total: indexedFileCount })
  }

  const content = (
    <div
      className={cn(
        "text-muted-foreground flex items-center gap-1.5 text-sm",
        onClick &&
          "hover:bg-accent hover:text-accent-foreground h-9 cursor-pointer rounded-full px-3 transition-colors",
        className
      )}
    >
      <div
        className={cn(
          "h-2 w-2 rounded-full transition-colors",
          isEmbeddingInProgress && "animate-pulse-fast bg-primary-ink",
          !isEmbeddingInProgress && hasLegacyFiles && "bg-destructive-ink",
          !isEmbeddingInProgress &&
            !hasLegacyFiles &&
            hasFilesNotInVectorStore &&
            (isReindexPolling ? "animate-pulse-fast bg-warning-ink" : "bg-warning-ink"),
          !isEmbeddingInProgress && !hasIssues && allFilesAreAnalysisReady && "bg-secondary",
          !isEmbeddingInProgress && !hasIssues && !allFilesAreAnalysisReady && "bg-success-ink"
        )}
      />
      <span
        className={cn(
          (isEmbeddingInProgress || (!hasLegacyFiles && isReindexPolling)) &&
            "animate-shimmer from-muted-foreground via-foreground/50 to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent"
        )}
      >
        {statusText}
      </span>
    </div>
  )

  let tooltipText: string
  if (hasLegacyFiles) {
    tooltipText = t("legacy_files_tooltip")
  } else if (isReindexPolling) {
    tooltipText = t("vector_store_expired_tooltip")
  } else if (hasFilesNotInVectorStore) {
    tooltipText = t("vector_store_needs_reindex_tooltip")
  } else if (allFilesAreAnalysisReady) {
    tooltipText = t("status_analysis_only")
  } else {
    tooltipText = t("project_files")
  }

  if (onClick) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              onClick()
            }}
          >
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  )
}
